import React from "react";
import { Link } from "react-router-dom";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { Section, SectionLabel, SectionTitle } from "@/components/layout/Section";
import { Reveal } from "@/lib/motion";
import { Button } from "@/components/ui/button";
import { Rocket, ShieldCheck, BrainCircuit, Code2, Send, ArrowRight, Handshake, Users, Building2, HeartHandshake } from "lucide-react";

const HERO_IMG = "/hero-legacy-league.jpg";
const HERO_IMG_WEBP = "/hero-legacy-league.webp";

const topics = [
  { icon: Send, title: "Entrepreneurship", body: "Developing the mindset to recognize opportunity and create value." },
  { icon: BrainCircuit, title: "AI Leverage", body: "Learning how to responsibly use AI to accelerate creativity, research, and execution." },
  { icon: ShieldCheck, title: "Intellectual Property Fundamentals", body: "Understanding how ideas become protected assets through patents, trademarks, copyrights, and ownership." },
  { icon: Code2, title: "Software-Driven Problem Solving", body: "Exploring how digital products and software can solve real-world challenges." },
  { icon: Rocket, title: "Venture Creation", body: "Transforming ideas into sustainable businesses with purpose and long-term impact." },
];

const engagement = [
  { icon: HeartHandshake, title: "Support the Mission", body: "Help expand access through a contribution to Legacy League.", cta: "Support the Mission", to: "/donate" },
  { icon: Handshake, title: "Partner With Us", body: "Bring Legacy League to your school, organization, or community.", cta: "Let’s Connect", to: "/contact" },
  { icon: Users, title: "Volunteer", body: "Share your time, experience, or mentorship.", cta: "Get Involved", to: "/contact" },
  { icon: Building2, title: "Become a Sponsor", body: "Help fund technology, programming, and student opportunities.", cta: "Partner With Us", to: "/contact" },
];

/**
 * MichiganGraphic renders the official uploaded Michigan illustration inside
 * a design-language frame: dark elevated surface with subtle blue glow,
 * BRP typographic caption for the Detroit label. The image itself already
 * highlights Detroit; the framing brings it into brand rhythm.
 */
function MichiganGraphic() {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-border/70 bg-[hsl(var(--brp-surface-2))] w-full max-w-xl">
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(65% 60% at 72% 68%, rgba(59,130,246,0.22) 0%, rgba(11,13,18,0) 65%)" }} />
      <img
        src="/michigan-map.png"
        alt="Michigan with Detroit highlighted as the inaugural launch location"
        className="relative w-full h-auto block"
        loading="lazy"
      />
      <div className="pointer-events-none absolute top-4 left-4 flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-[hsl(var(--brp-blue))]">
        <span className="h-px w-6 bg-[hsl(var(--brp-blue))]" />
        Inaugural Cohort — Fall 2026
      </div>
    </div>
  );
}

export default function LegacyLeaguePage() {
  return (
    <SiteLayout>
      {/* HERO */}
      <section className="relative min-h-[560px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <picture>
            <source srcSet={HERO_IMG_WEBP} type="image/webp" />
            <img src={HERO_IMG} alt="Legacy League — building the next generation of founders" className="h-full w-full object-cover" loading="eager" />
          </picture>
          <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/60 to-black/95" />
          <div aria-hidden className="absolute inset-0 brp-hero-glow-orange opacity-70" />
        </div>
        <div className="relative mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-10 py-20 sm:py-28">
          <Reveal>
            <div className="text-[11px] tracking-[0.28em] uppercase text-[hsl(var(--brp-orange))]">Legacy League</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-6 font-display font-semibold tracking-[-0.03em] text-4xl sm:text-6xl lg:text-7xl leading-[1.02] max-w-3xl">
              Entrepreneurship as the First Career Choice<span className="text-[hsl(var(--brp-orange))]">.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-xl text-[hsl(var(--brp-ink-dim))] leading-relaxed">
              A founder development initiative preparing students to think bigger, solve meaningful problems, and create lasting impact.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#core-areas">
                <Button className="rounded-full bg-[hsl(var(--brp-orange))] hover:bg-[#EA580C] text-black px-7 h-12 font-medium">
                  Explore the Initiative <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
              <a href="#engagement">
                <Button variant="outline" className="rounded-full border-border/70 text-[hsl(var(--brp-ink))] hover:bg-white/5 px-7 h-12">
                  Support the Mission
                </Button>
              </a>
              <Link to="/donate">
                <Button className="rounded-full bg-[hsl(var(--brp-blue))] hover:bg-[#2563EB] text-white px-7 h-12 font-medium">
                  Donate <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CORE AREAS */}
      <Section tone="dark" py="lg" className="border-t border-border/70">
        <div id="core-areas" className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <SectionLabel tone="orange">Core Areas of Discovery</SectionLabel>
            <SectionTitle className="mt-4" size="xl">
              Building founders begins with expanding perspective<span className="text-[hsl(var(--brp-orange))]">.</span>
            </SectionTitle>
            <p className="mt-6 text-[hsl(var(--brp-ink-dim))] leading-relaxed">
              Students are introduced to the disciplines, technologies, and ways of thinking that shape modern entrepreneurship.
            </p>
          </Reveal>
          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            {topics.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.04}>
                <div className="h-full rounded-xl border border-border/70 bg-[hsl(var(--brp-surface-2))] p-5 hover:border-[hsl(var(--brp-orange))]/50 transition-colors">
                  <div className="h-9 w-9 rounded-lg border border-border/70 bg-background/40 grid place-items-center text-[hsl(var(--brp-orange))]">
                    <t.icon className="h-4 w-4" />
                  </div>
                  <h3 className="mt-4 text-sm font-display font-semibold leading-snug">{t.title}</h3>
                  <p className="mt-2 text-xs text-[hsl(var(--brp-ink-dim))] leading-relaxed">{t.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* DETROIT */}
      <Section tone="dark" py="lg">
        <div className="rounded-2xl border border-border/70 bg-[hsl(var(--brp-surface-2))] p-6 sm:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <Reveal className="lg:col-span-6 flex justify-center">
              <MichiganGraphic />
            </Reveal>
            <Reveal delay={0.08} className="lg:col-span-6">
              <SectionLabel tone="orange">Launching in Detroit</SectionLabel>
              <SectionTitle className="mt-4" size="lg">The Journey Begins in Detroit<span className="text-[hsl(var(--brp-orange))]">.</span></SectionTitle>
              <p className="mt-4 text-[hsl(var(--brp-ink-dim))] leading-relaxed">
                Legacy League is launching its inaugural founder development initiative in the Detroit Metropolitan Area during the <span className="text-[hsl(var(--brp-orange))]">2026–2027</span> school year.
              </p>
              <p className="mt-4 text-[hsl(var(--brp-ink-dim))] leading-relaxed">
                This first implementation represents the beginning of a long-term vision to expand access to entrepreneurship, innovation, and opportunity for the next generation.
              </p>
            </Reveal>
          </div>
        </div>
      </Section>

      {/* ENGAGEMENT (NO DONATION) */}
      <Section id="engagement" tone="dark" py="lg">
        <Reveal>
          <SectionLabel tone="blue">Join the Initiative</SectionLabel>
          <SectionTitle className="mt-4 max-w-3xl" size="xl">Help Shape What’s Next<span className="text-[hsl(var(--brp-orange))]">.</span></SectionTitle>
          <p className="mt-6 max-w-3xl text-[hsl(var(--brp-ink-dim))] leading-relaxed">
            Whether you’d like to volunteer your expertise, explore a community partnership, or learn more about bringing Legacy League to your organization, we’d love to start the conversation.
          </p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {engagement.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.04}>
              <div data-testid="legacy-engagement-card" className="group h-full rounded-2xl border border-border/70 bg-[hsl(var(--brp-surface-2))] p-6 hover:border-[hsl(var(--brp-blue))]/50 transition-colors">
                <div className="h-10 w-10 rounded-lg border border-border/70 bg-background/40 grid place-items-center text-[hsl(var(--brp-blue))]">
                  <c.icon className="h-4 w-4" />
                </div>
                <h3 className="mt-5 text-lg font-display font-semibold">{c.title}</h3>
                <p className="mt-2 text-sm text-[hsl(var(--brp-ink-dim))] leading-relaxed">{c.body}</p>
                <Link to={c.to} className="mt-6 inline-flex items-center gap-2 text-[13px] tracking-wide uppercase text-[hsl(var(--brp-blue))] hover:text-[hsl(var(--brp-blue-2))]">
                  {c.cta} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* CLOSING */}
      <Section tone="paper" py="lg">
        <div className="text-center flex flex-col items-center gap-8">
          <Reveal><SectionTitle size="xl" className="max-w-3xl">Every founder begins with a first opportunity.</SectionTitle></Reveal>
          <Reveal delay={0.05}><p className="text-[hsl(var(--brp-ink-muted-on-paper))] max-w-xl leading-relaxed">Legacy League exists to make sure that first opportunity is accessible, meaningful, and built to last.</p></Reveal>
          <Reveal delay={0.1}>
            <Link to="/contact">
              <Button size="lg" className="rounded-full bg-[hsl(var(--brp-ink-on-paper))] hover:bg-black text-white px-8 h-12">
                Let’s Talk <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </Reveal>
        </div>
      </Section>
    </SiteLayout>
  );
}
